// Story 8.5 — GET /api/v1/creators/me/payout/settlements.
//
// The creator's INVOICE ARCHIVE. Each row is a self-billed invoice Greenhold
// raised on their behalf: the period, the earning lines, the fee and the net.
//
// It carries NO VAT field, deliberately (owner ruling 2026-09-04,
// docs/creator-share-net-proceeds-2026-09-04.md §1.2): the word never appears on
// a creator-facing settlement or statement surface, in either registration
// phase. The server stopped serving `vat_treatment` when F13 closed; do not
// re-add it to this type or render it. Guarded server-side by
// creator_payout_service::tests::creator_facing_settlement_dtos_never_state_vat.
//
// It is served from the stored `creator_payout_settlements` row rather than
// reconstructed, because the stored row is the authoritative artifact. The
// supplier characterisation depends on an invoice existing, and an invoice
// whose only copy is in someone's inbox is one bounce away from not existing.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** `queued` — invoiced, earnings debited, waiting for the period's cut-off.
 *  `initiated` — the sweep has called the provider.
 *  `paid` — the provider accepted; money on its way.
 *  `compensated` — undeliverable, and the gross went back to the earnings pot.
 *  `failed` — undeliverable AND the reversal failed. A human is on it. */
export type SettlementStatus =
  | "queued"
  | "initiated"
  | "paid"
  | "compensated"
  | "failed";

export type SettlementLine = {
  line_kind: "creator_primary_share" | "creator_royalty";
  amount_cents: number;
};

export type Settlement = {
  id: string;
  invoice_number: string;
  invoice_issued_at: string;
  period_start: string;
  period_end: string;
  rail: "bank" | "card";
  gross_cents: number;
  fee_cents: number;
  net_cents: number;
  status: SettlementStatus;
  destination_last4: string | null;
  arrival_date: string | null;
  settled_at: string | null;
  lines: SettlementLine[];
};

export const SETTLEMENTS_QUERY_KEY = ["creator-payout-settlements"] as const;

async function fetchSettlements(): Promise<Settlement[]> {
  const response = await fetch("/api/v1/creators/me/payout/settlements", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`settlements fetch failed: ${response.status}`);
  }
  const body = (await response.json()) as { data?: Settlement[] };
  return body.data ?? [];
}

export function useSettlements() {
  return useQuery({
    queryKey: SETTLEMENTS_QUERY_KEY,
    queryFn: fetchSettlements,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
